import { EVENT_DEFAULT_MINUTES } from './events'
import { addDaysStr } from './mustDo'

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const

const pad = (n: number) => String(n).padStart(2, '0')

function ymd(y: number, m: number, d: number): string | null {
  const dt = new Date(Date.UTC(y, m - 1, d))
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
    ? `${y}-${pad(m)}-${pad(d)}`
    : null
}

const weekdayOf = (day: string) => new Date(`${day}T00:00:00Z`).getUTCDay()

export const weekdayName = (day: string) => WEEKDAY_NAMES[weekdayOf(day)]

// Next MM-DD on or after `today`; skips years without it (Feb 29).
function nextMonthDay(m: number, d: number, today: string): string | null {
  const year = Number(today.slice(0, 4))
  for (let y = year; y <= year + 4; y++) {
    const s = ymd(y, m, d)
    if (s && s >= today) return s
  }
  return null
}

// The model reports dates in the form it heard them; resolving them here keeps
// weekday and year arithmetic out of the model. Forms: YYYY-MM-DD, MM-DD (no year
// said), +N (days from today), mon…sun (next such day after today), D (day of month).
export function resolveDueDate(raw: string | null | undefined, today: string): string | null {
  const s = raw?.trim().toLowerCase()
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(today)) return null
  let m: RegExpMatchArray | null
  if ((m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) {
    const date = ymd(Number(m[1]), Number(m[2]), Number(m[3]))
    if (!date) return null
    // A past date is the model defaulting to the wrong year.
    return date >= today ? date : nextMonthDay(Number(m[2]), Number(m[3]), today)
  }
  if ((m = s.match(/^(\d{1,2})-(\d{1,2})$/))) return nextMonthDay(Number(m[1]), Number(m[2]), today)
  if ((m = s.match(/^\+(\d{1,3})$/))) return addDaysStr(today, Number(m[1]))
  const wd = s.length >= 3 ? WEEKDAY_NAMES.findIndex((n) => n.toLowerCase().startsWith(s)) : -1
  if (wd >= 0) return addDaysStr(today, ((wd - weekdayOf(today) + 6) % 7) + 1)
  if ((m = s.match(/^(\d{1,2})$/))) {
    const d = Number(m[1])
    let y = Number(today.slice(0, 4))
    let mo = Number(today.slice(5, 7))
    for (let i = 0; i < 13; i++) {
      const date = ymd(y, mo, d)
      if (date && date >= today) return date
      if (++mo > 12) {
        mo = 1
        y++
      }
    }
  }
  return null
}

export function resolveTime(raw: string | null | undefined): string | null {
  const m = raw?.trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/)
  return m ? `${pad(Number(m[1]))}:${m[2]}` : null
}

export function resolveDuration(raw: number | null | undefined): number | null {
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) return null
  return Math.min(1440, Math.max(5, Math.round(raw)))
}

// A specific due date supersedes "this week"; today/tomorrow stay, since those
// mean the user committed to doing it then (the Do today lane), not a deadline.
export function captureWhen(
  when: 'today' | 'tomorrow' | 'thisWeek' | 'none',
  dueDate: string | null,
): 'today' | 'tomorrow' | 'thisWeek' | null {
  if (when === 'none' || (when === 'thisWeek' && dueDate)) return null
  return when
}

export function resolveCaptureSchedule(
  it: {
    when: 'today' | 'tomorrow' | 'thisWeek' | 'none'
    dueDate?: string | null
    time?: string | null
    durationMinutes?: number | null
  },
  today: string,
) {
  const time = resolveTime(it.time)
  // A time with no day ("call mom at 3pm") means today.
  const dueDate = resolveDueDate(it.dueDate, today) ?? (time ? today : null)
  return {
    when: captureWhen(it.when, dueDate),
    dueDate,
    time: dueDate ? time : null,
    durationMinutes: resolveDuration(it.durationMinutes),
  }
}

export function cleanLocation(raw: string | null | undefined): string | null {
  const s = raw?.trim().replace(/\s+/g, ' ')
  return s ? s.slice(0, 200) : null
}

const localDate = (day: string, time?: string | null) => {
  const [y, m, d] = day.split('-').map(Number)
  const [h, min] = time ? time.split(':').map(Number) : [12, 0]
  return new Date(y, m - 1, d, h, min)
}

// Clients: createQuickTask due fields for a captured task, in the device's time
// zone. All-day dates sit at local noon (matches the inbox composer).
export function captureDueArgs(it: {
  dueDate: string | null
  time: string | null
  durationMinutes: number | null
}): { dueDate?: number; durationMinutes?: number; allDay?: boolean } {
  if (!it.dueDate) return {}
  const dueDate = localDate(it.dueDate, it.time).getTime()
  if (it.time) return { dueDate, durationMinutes: it.durationMinutes ?? 60 }
  return {
    dueDate,
    ...(it.durationMinutes && { durationMinutes: it.durationMinutes, allDay: true }),
  }
}

// "Sat, Nov 9" all-day, "Nov 9, 14:00" timed; the year only when it isn't this one.
export function captureDueLabel(dueDate: string, time: string | null, locale: string) {
  const date = localDate(dueDate, time)
  const otherYear = date.getFullYear() !== new Date().getFullYear()
  const day = date.toLocaleDateString(locale, {
    ...(!time && { weekday: 'short' }),
    month: 'short',
    day: 'numeric',
    ...(otherYear && { year: 'numeric' }),
  })
  return time ? `${day}, ${time}` : day
}

export const mapsSearchUrl = (location: string) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`

// Events need a day: one with none said is really a task ("dentist" → call to
// book). No time → all-day; a time with no length → the 45-min default.
export function resolveCaptureEvent(
  it: {
    when: 'today' | 'tomorrow' | 'thisWeek' | 'none'
    dueDate?: string | null
    time?: string | null
    durationMinutes?: number | null
  },
  today: string,
) {
  const s = resolveCaptureSchedule(it, today)
  if (!s.dueDate) return { kind: 'task' as const, ...s, allDay: false }
  return {
    kind: 'event' as const,
    when: null,
    dueDate: s.dueDate,
    time: s.time,
    durationMinutes: s.time ? (s.durationMinutes ?? EVENT_DEFAULT_MINUTES) : null,
    allDay: !s.time,
  }
}

// A task the model tied to an event in the same dump ("book flights" → the
// conference): kept only when it points at a dated event and the task has no
// date of its own. The client offers "Due before <event day>?", off by default.
export function resolveRelatedEvents(
  items: { kind: string; dueDate: string | null; relatedEvent?: number | null }[],
): (number | null)[] {
  return items.map((it, i) => {
    const j = it.relatedEvent
    if (it.kind !== 'task' || it.dueDate || j == null || j === i) return null
    const target = items[j]
    return target?.kind === 'event' && target.dueDate ? j : null
  })
}

// Clients: event start in the device's time zone (all-day = local midnight).
export function captureEventStart(dueDate: string, time: string | null) {
  const [y, m, d] = dueDate.split('-').map(Number)
  const [h, min] = time ? time.split(':').map(Number) : [0, 0]
  return new Date(y, m - 1, d, h, min).getTime()
}
